import {useState, useEffect, useCallback} from 'react';
import {toast} from 'react-toastify';
import {ErrorMessage} from '@hookform/error-message';
import {useForm, Controller} from 'react-hook-form';
import FormInput from '../../components/FoodMenu/FormInput';
import FormMultiSelectInput from '../../components/Settings/DropdownMultiSelectInput';
import {
    updateCateringSettings,
    getCateringSettings,
    getAllCuisineList,
    getAllOccasionList,
} from '../../../services/SettingsService';
import TimePicker from '../../components/Settings/TimePicker';
import {objectToFormData} from '../../../utils/objectToFormData';

const notify = (id, type, message, time) => {
    toast.update(id, {
        render: message,
        type: type,
        position: 'top-center',
        autoClose: time,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        isLoading: false,
    });
};

const CateringSettings = () => {
    const [catdetails, setCatdetails] = useState();
    const [cuisineList, setCuisineList] = useState();
    const [occasionList, setOccasionList] = useState();
    const [live, setLive] = useState(false);

    const {
        handleSubmit,
        formState: {errors},
        register,
        setValue,
        control,
    } = useForm({
        mode: 'all',
        defaultValues: {
            cat_start_time:
                new Date('2000-01-01 10:00 AM').getHours() +
                ':' +
                String(new Date('2000-01-01 10:00 AM').getMinutes()).padStart(2, '0'),
            cat_end_time:
                new Date('2000-01-01 11:00 PM').getHours() +
                ':' +
                String(new Date('2000-01-01 11:00 PM').getMinutes()).padStart(2, '0'),
        },
    });

    const getCuisineList = useCallback(() => {
        getAllCuisineList().then(res => {
            res.forEach(each => {
                Object.defineProperty(
                    each,
                    'label',
                    Object.getOwnPropertyDescriptor(each, 'name')
                );
                Object.defineProperty(
                    each,
                    'value',
                    Object.getOwnPropertyDescriptor(each, 'id')
                );
            });
            let mappedCuisineList = res
                .map(each => {
                    each.value = +each.value;
                    return each;
                })
                .filter(each => each.status);
            setCuisineList(mappedCuisineList);
        });
    }, [setCuisineList]);

    const getOccasionList = useCallback(() => {
        getAllOccasionList().then(res => {
            res.forEach(each => {
                Object.defineProperty(
                    each,
                    'label',
                    Object.getOwnPropertyDescriptor(each, 'occassion')
                );
                Object.defineProperty(
                    each,
                    'value',
                    Object.getOwnPropertyDescriptor(each, 'id')
                );
            });
            let mappedOccasionList = res.map(each => {
                each.value = +each.value;
                return each;
            });

            setOccasionList(mappedOccasionList);
        });
    }, [setOccasionList]);

    useEffect(() => {
        getCuisineList();
        getOccasionList();

        getCateringSettings().then(res => {
            setCatdetails(res);
        });
    }, [getCuisineList, getOccasionList]);

    const days = [
        {name: 'catering_days.son', label: 'Sunday'},
        {name: 'catering_days.mon', label: 'Monday'},
        {name: 'catering_days.tue', label: 'Tuesday'},
        {name: 'catering_days.wed', label: 'Wednesday'},
        {name: 'catering_days.thu', label: 'Thursday'},
        {name: 'catering_days.fri', label: 'Friday'},
        {name: 'catering_days.sat', label: 'Saturday'},
    ];

    useEffect(() => {
        if (catdetails) {
            const fields = [
                'cat_start_time',
                'cat_end_time',
                'catering_prior_days',
                'is_catering',
                'catering_time',
                'catering_prior_time',
                'is_pre_cook',
                'is_live_cook',
                'catering_days',
                'cuisine_types',
                'occasion',
                'indoor_service',
                'outdoor_service',
            ];

            fields.forEach(field => setValue(field, catdetails[field]));

            // Set the live state based on the data from API
            if (catdetails.is_live_cook !== undefined) {
                setLive(catdetails.is_live_cook);
            }

            if (catdetails.cuisine_list) {
                // Handle both array formats: [string] or [[id, name]]
                const cuisineValues = catdetails.cuisine_list.map(cuisine => {
                    // Check if cuisine is an array (old format) or string (new format)
                    if (Array.isArray(cuisine)) {
                        // Dynamic mapping - use index 0 for value and index 1 for label if available
                        return {
                            value: cuisine[0] || cuisine,
                            label: cuisine[1] || cuisine[0] || cuisine,
                        };
                    } else {
                        // New format: simple string array
                        return {
                            value: cuisine,
                            label: cuisine,
                        };
                    }
                });
                setValue('cuisine_list', cuisineValues);
            }

            if (catdetails.occassion_list) {
                // Handle both array formats: [string] or [[id, name]]
                const occasionValues = catdetails.occassion_list.map(occasion => {
                    // Check if occasion is an array (old format) or string (new format)
                    if (Array.isArray(occasion)) {
                        // Dynamic mapping - use index 0 for value and index 1 for label if available
                        return {
                            value: occasion[0] || occasion,
                            label: occasion[1] || occasion[0] || occasion,
                        };
                    } else {
                        // New format: simple string array
                        return {
                            value: occasion,
                            label: occasion,
                        };
                    }
                });
                setValue('occassion_list', occasionValues);
            }
        }
    }, [catdetails, setValue]);

    const onSubmit = async data => {
        // Instead of IDs, extract the 'label' (name) for cuisines and occasions
        data.cuisine_list = (data.cuisine_list || []).map(item => item.label);
        data.occassion_list = (data.occassion_list || []).map(item => item.label);
    
        console.log(data, 'Catering Settings updating');
    
        const toaster = toast.loading('Updating...');
        data.is_live_cook = live;
        data.is_pre_cook = !live;
    
        let postData = objectToFormData(data);
        let res = await updateCateringSettings(postData);
        if (res.status === 'success') {
            notify(toaster, 'success', 'Updated Catering Settings!', 2000);
        } else {
            notify(toaster, 'error', res.data.message, 2000);
        }
    };
    

    return (
        <>
            <div className='modal-body'>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='d-flex row'>
                        <div className='col-md-6 col-xl-3 col-xxl-6 mb-3 picker-data'>
                            <div className='color-time-picker'>
                                <TimePicker
                                    label={'Start time'}
                                    name={'cat_start_time'}
                                    required={true}
                                    Controller={Controller}
                                    control={control}
                                    errors={errors}
                                    id={'cat_start_time'}
                                    ErrorMessage={ErrorMessage}
                                    register={{
                                        ...register('cat_start_time'),
                                        required: 'Catering Start time is required!',
                                    }}
                                />
                            </div>
                        </div>
                        <div className='col-md-6 col-xl-3 col-xxl-6 mb-3 picker-data'>
                            <div className='color-time-picker '>
                                <TimePicker
                                    label={'End time'}
                                    name={'cat_end_time'}
                                    Controller={Controller}
                                    control={control}
                                    errors={errors}
                                    id={'cat_end_time'}
                                    required={true}
                                    ErrorMessage={ErrorMessage}
                                    register={{
                                        ...register('cat_end_time'),
                                        required: 'Catering End time is required!',
                                    }}
                                />
                            </div>
                        </div>

                        <div className='col-10'>
                            <FormInput
                                label={'Minimum Catering Prior Days'}
                                id={'catering_prior_days'}
                                name={'catering_prior_days'}
                                required={true}
                                ErrorMessage={ErrorMessage}
                                register={{
                                    ...register('catering_prior_days'),
                                    required: 'Catering Prior Days is required!',
                                }}
                                errors={errors}
                            />
                        </div>
                        <div className='col-10'>
                            <FormMultiSelectInput
                                label={'occasion'}
                                id={'val-occasion'}
                                name={'occassion_list'}
                                ErrorMessage={ErrorMessage}
                                Controller={Controller}
                                control={control}
                                errors={errors}
                                required={true}
                                options={
                                    occasionList
                                        ? occasionList
                                        : [
                                            {
                                                value: null,
                                                label: 'Loading...',
                                            },
                                        ]
                                }
                            />
                        </div>
                        <div className='col-10'>
                            <FormMultiSelectInput
                                label={'Available Cuisines'}
                                id={'val-cuisine'}
                                name={'cuisine_list'}
                                ErrorMessage={ErrorMessage}
                                Controller={Controller}
                                control={control}
                                errors={errors}
                                required={true}
                                options={
                                    cuisineList
                                        ? cuisineList
                                        : [
                                            {
                                                value: null,
                                                label: 'Loading...',
                                            },
                                        ]
                                }
                            />
                        </div>

                        <label className='text-black font-w500 col-12'>Available days</label>
                        {days.map((day, i) => (
                            <div className='col-4' key={day.name}>
                                <div className='custom-control custom-checkbox mb-3 checkbox-info'>
                                    <input
                                        type='checkbox'
                                        defaultChecked
                                        className='custom-control-input'
                                        {...register(day.name)}
                                        id={day.name}
                                    />
                                    <label className='custom-control-label' htmlFor={day.name}>
                                        {day.label}
                                    </label>
                                </div>
                            </div>
                        ))}

                        <div className='form-group mb-3 size-filter col-10'>
                            <div className='size-filter'>
                                <div className='btn-group col-10' data-toggle='buttons'>
                                    <label
                                        onClick={() => setLive(true)}
                                        className={`btn btn-outline-success light btn-sm ${
                                            live ? 'active' : ''
                                        } `}
                                    >
                                        <input
                                            type='radio'
                                            className='position-absolute invisible'
                                            value={live}
                                            name='is_live_cook'
                                            id='is_live_cook'
                                        />
                                        Live Cook
                                    </label>
                                    <label
                                        onClick={() => setLive(false)}
                                        className={`btn btn-outline-warning light btn-sm ${
                                            !live ? 'active' : ''
                                        } `}
                                    >
                                        <input
                                            type='radio'
                                            className='position-absolute invisible'
                                            value={!live}
                                            name='is_pre_cook'
                                            id='is_pre_cook'
                                        />
                                        Pre Cook
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className='col-12 mb-3'>
                            <label className='text-black font-w500'>Service Options</label>
                            <div className='row'>
                                <div className='col-6'>
                                    <div className='custom-control custom-checkbox mb-3 checkbox-info'>
                                        <input
                                            type='checkbox'
                                            className='custom-control-input'
                                            {...register('indoor_service')}
                                            id='indoor_service'
                                        />
                                        <label className='custom-control-label' htmlFor='indoor_service'>
                                            Indoor Service
                                        </label>
                                    </div>
                                </div>
                                <div className='col-6'>
                                    <div className='custom-control custom-checkbox mb-3 checkbox-info'>
                                        <input
                                            type='checkbox'
                                            className='custom-control-input'
                                            {...register('outdoor_service')}
                                            id='outdoor_service'
                                        />
                                        <label className='custom-control-label' htmlFor='outdoor_service'>
                                            Outdoor Service
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button className='btn btn-primary btn-block btn-md mb-2 mr-auto'>Update</button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default CateringSettings;